import throttle from "lodash-es/throttle";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  query,
  where,
  limit,
  orderBy,
  getDocs,
  collection,
  getFirestore,
} from "firebase/firestore";
import { RootState } from "app/store";
import { fetchAuthToken, parentsFirebase } from "app/Auth";
import { client } from "common/apiClient";
import { ProjectEntity } from "features/creator/types";
import { ChatRoom, ChatRoomType } from "features/user/types";
import { ApiPieceComment, ApiPieceListItem } from "features/builder/types";
import {
  AccountProfile,
  FeedbackHistoryItem,
  GetActivitiesResponse,
  GetAIChatSessionsResponse,
  GetAIChatMessagesResponse,
  ParentPieceComment,
  ParentPieceReport,
} from "features/parent/types";

// ============================================================
// API 関数
// ============================================================

/**
 * フィードバック履歴取得
 */
export const getFeedbackHistory = async ({
  uid,
  limit = 20,
  offset = 0,
}: {
  uid: string;
  limit?: number;
  offset?: number;
}): Promise<FeedbackHistoryItem[]> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get<FeedbackHistoryItem[]>(
    `/v1/review/history/${uid}?limit=${limit}&offset=${offset}`,
  );
  return response.data;
};

/**
 * ユーザー活動履歴取得
 */
export const getUserActivities = async ({
  parentUid,
  childUid,
  limit = 5,
}: {
  parentUid: string;
  childUid: string;
  limit?: number;
}): Promise<GetActivitiesResponse> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get<GetActivitiesResponse>(
    `/v1/user/activity/${parentUid}/child/${childUid}?limit=${limit}`,
  );
  return response.data;
};

/**
 * 子どものAIチャットセッション一覧取得
 */
export const getChildAIChatSessions = async ({
  parentUid,
  childUid,
  limit = 20,
  offset = 0,
}: {
  parentUid: string;
  childUid: string;
  limit?: number;
  offset?: number;
}): Promise<GetAIChatSessionsResponse> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get<GetAIChatSessionsResponse>(
    `/v1/parents/${parentUid}/children/${childUid}/ai-chat/sessions?limit=${limit}&offset=${offset}`,
  );
  return response.data;
};

/**
 * AIチャットセッションのメッセージ取得
 */
export const getAIChatMessages = async ({
  parentUid,
  sessionId,
  limit = 50,
  beforeId,
}: {
  parentUid: string;
  sessionId: string;
  limit?: number;
  beforeId?: string;
}): Promise<GetAIChatMessagesResponse> => {
  const token = await fetchAuthToken();
  let url = `/v1/parents/${parentUid}/ai-chat/sessions/${sessionId}/messages?limit=${limit}`;
  if (beforeId) {
    url += `&before_id=${beforeId}`;
  }
  const response = await client({ token }).get<GetAIChatMessagesResponse>(url);
  return response.data;
};

// ============================================================
// チャット API (Redux Thunk)
// ============================================================

/**
 * 保護者用チャットルーム一覧取得
 * ※ user/slice と分離するために parent 専用の thunk として定義
 */
export const getParentChatRooms = createAsyncThunk(
  "parent/getParentChatRooms",
  async ({ uid }: { uid: string }) => {
    const token = await fetchAuthToken();
    const response = await client({ token }).get(`/v1/chat/room/${uid}`);
    return response.data.data;
  },
);

/**
 * 保護者のチャットルーム一覧を取得（差分追加用）
 * Sliceを更新しない単純なAPI呼び出し
 */
export const fetchParentChatRooms = async ({ uid }: { uid: string }) => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/chat/room/${uid}`);
  return response.data.data.chat_rooms as ChatRoom[];
};

export const getParentChatRoomMessages = createAsyncThunk(
  "parent/getParentChatRoomMessages",
  async (
    {
      roomId,
      lastMessageSyncAt,
      messageCount,
      chatRoomType,
    }: {
      roomId: string;
      lastMessageSyncAt: string;
      messageCount: number;
      chatRoomType: ChatRoomType;
    },
    thunkAPI,
  ) => {
    const state = thunkAPI.getState() as RootState;
    const db = getFirestore(parentsFirebase);

    // talk room 以外は制限チェックしない
    if (chatRoomType !== ChatRoomType.TALK) {
      const queryMessage = query(
        collection(db, "rooms", roomId, "messages"),
        where("created_at", "<", lastMessageSyncAt),
        orderBy("created_at", "desc"),
        limit(messageCount),
      );
      const querySnapshot = await getDocs(queryMessage);
      const messages = querySnapshot.docs.map((doc) => doc.data());
      return { messages, limitReached: false };
    }

    // 以下は talk room の場合のみ（制限対象）
    const chatHistoryLimit = state.subscription.accountLimits.chatHistoryLimit;
    const limitValue = chatHistoryLimit?.limit ?? -1;
    const used = chatHistoryLimit?.used ?? 0;
    const unlimited = chatHistoryLimit?.unlimited ?? false;

    // 制限チェック（-1 は無制限、unlimited も無制限）
    if (!unlimited && limitValue !== -1 && used >= limitValue) {
      return { messages: [], limitReached: true };
    }

    const queryMessage = query(
      collection(db, "rooms", roomId, "messages"),
      where("created_at", "<", lastMessageSyncAt),
      orderBy("created_at", "desc"),
      limit(messageCount),
    );
    const querySnapshot = await getDocs(queryMessage);
    const messages = querySnapshot.docs.map((doc) => doc.data());

    return { messages, limitReached: false };
  },
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const markParentChatRoomMessage = createAsyncThunk<any, void>(
  "parent/markParentChatRoomMessage",
  async (_, thunkAPI) => {
    const token = await fetchAuthToken();
    const { parent } = thunkAPI.getState() as RootState;
    const chatRoomsReadAt = parent.chatRoomMessageSync.ids
      .map((id) => parent.chatRoomMessageSync.entities[id])
      .filter(
        (sync) =>
          sync.lastReadAt !== sync.latestReadAt ||
          (sync.lastReadAt === null && sync.latestReadAt !== null),
      )
      .map((sync) => ({
        chat_room_id: sync.id,
        uid: sync.uid,
        last_read_at: sync.latestReadAt,
      }));
    const response = await client({ token: token }).put(`/v1/chat/room/marked`, {
      chatRoomsReadAt,
    });
    return response.data.data;
  },
);

/**
 * ユーザーのプロジェクト一覧取得
 */
export const getUserProjects = async (uid: string): Promise<ProjectEntity[]> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/projects/user/${uid}`);
  return response.data;
};

/**
 * 子どものピース一覧取得
 */
export const getUserPieces = async (uid: string): Promise<ApiPieceListItem[]> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/pieces/user/${uid}`);
  return response.data;
};

/**
 * 子どもが書いたピースのコメント一覧取得
 */
export const getUserPieceComments = async (uid: string): Promise<ParentPieceComment[]> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/pieces/user/${uid}/comments`);
  return response.data;
};

/**
 * 子どもが出した通報の履歴
 */
export const getUserPieceReports = async (uid: string): Promise<ParentPieceReport[]> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/pieces/user/${uid}/reports`);
  return response.data;
};

/**
 * ピース1件の取得（子どものピースに無いもの＝他人のピースも見られるように）
 */
export const getPiece = async (pieceId: string): Promise<ApiPieceListItem> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/pieces/${pieceId}`);
  return response.data;
};

/**
 * アプリ1件の取得（通報したあとにブロックされ、一覧から消えたアプリも見られるように）
 */
export const getProject = async (projectId: string): Promise<ProjectEntity> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/projects/${projectId}`);
  return response.data;
};

/**
 * ピースに付いたコメント一覧
 */
export const getPieceComments = async (pieceId: string): Promise<ApiPieceComment[]> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/pieces/${pieceId}/comments?limit=50`);
  return response.data;
};

/**
 * ユーザーステータス取得
 */
export const getUserStatus = async (uid: string) => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/user/status/${uid}`);
  return response.data.data;
};

// ============================================================
// プロフィール API
// ============================================================

/**
 * 保護者プロフィール取得
 */
export const getAccountProfile = createAsyncThunk(
  "parent/getAccountProfile",
  async ({ uid }: { uid: string }) => {
    const token = await fetchAuthToken();
    const response = await client({ token }).get(`/v1/user/profile/${uid}`);
    return response.data;
  },
);

/**
 * 保護者プロフィール更新
 */
export const updateAccountProfile = createAsyncThunk(
  "parent/updateAccountProfile",
  throttle(async ({ uid, profile }: { uid: string; profile: Partial<AccountProfile> }) => {
    const token = await fetchAuthToken();
    const response = await client({ token }).put(`/v1/user/profile/${uid}`, profile);
    return response.data;
  }, 1000),
);
